import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n).toFixed(2)}`;

export default function Checkout() {
    const { items, subtotal } = useCart();
    const { user } = useAuth();
    const { state } = useLocation();
    const nav = useNavigate();
    const appliedPromo = state?.appliedPromo;
    const [loading, setLoading] = useState(false);
    const [testMode, setTestMode] = useState(false);
    useEffect(() => {
        api.get("/config/public").then(r => setTestMode(String(r.data?.stripe_public_key || "").startsWith("pk_test_"))).catch(() => {});
    }, []);
    const [form, setForm] = useState({
        name: user?.name || "",
        email: user?.email || "",
        phone: "",
        address: "",
    });

    const discount = appliedPromo
        ? Math.min(subtotal, subtotal * ((appliedPromo.discount_percent || 0) / 100) + (appliedPromo.discount_amount || 0))
        : 0;
    const total = Math.max(0, subtotal - discount);

    if (items.length === 0) {
        return <div className="pt-32 text-center text-[#6B645C]">Cart is empty. <button className="text-gold underline" onClick={() => nav("/automotive-window-film")}>Continue shopping</button></div>;
    }

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/checkout/session", {
                items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
                promo_code: appliedPromo?.code || null,
                origin_url: window.location.origin,
                shipping_name: form.name, shipping_address: form.address, shipping_email: form.email, shipping_phone: form.phone,
            });
            window.location.href = data.url;
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Checkout failed. Please review your cart and try again.");
            setLoading(false);
        }
    };

    return (
        <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 md:px-12 py-16">
            <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Secure Checkout</div>
            <h1 className="font-display text-5xl mb-12">Complete your purchase</h1>

            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12">
                <form onSubmit={submit} className="space-y-4" data-testid="checkout-form">
                    <h2 className="font-display text-2xl mb-2">Shipping</h2>
                    <Input aria-label="Full name" required data-testid="checkout-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                    <Input aria-label="Email" required type="email" data-testid="checkout-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                    <Input aria-label="Phone" data-testid="checkout-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                    <Textarea aria-label="Full shipping address" required rows={3} data-testid="checkout-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full shipping address" className="bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                    <Button data-testid="pay-btn" type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold py-6">
                        {loading ? "Redirecting to Stripe…" : `Pay ${fmt(total)} with Stripe`}
                    </Button>
                    {testMode && (
                        <p data-testid="stripe-test-mode-note" className="text-xs text-[#6B645C] text-center">Test mode. Use card 4242 4242 4242 4242 with any future date & CVC.</p>
                    )}
                </form>

                <aside className="bg-charcoal-800 border border-[#E2D4C1] p-8 self-start">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-4">Order Summary</div>
                    <div className="space-y-3 text-sm max-h-72 overflow-auto">
                        {items.map(i => (
                            <div key={i.product_id} className="flex justify-between border-b border-[#E2D4C1] pb-2">
                                <span className="text-[#33302C] truncate pr-2">{i.name} ×{i.quantity}</span>
                                <span>{fmt(i.price * i.quantity)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-2 mt-6 text-sm">
                        <div className="flex justify-between"><span className="text-[#33302C]">Subtotal</span><span>{fmt(subtotal)}</span></div>
                        {appliedPromo && <div className="flex justify-between text-gold-light"><span>{appliedPromo.code}</span><span>-{fmt(discount)}</span></div>}
                        <div className="flex justify-between text-lg pt-3 border-t border-[#E2D4C1]"><span>Total</span><span className="text-gold font-medium">{fmt(total)}</span></div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
